'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createMessageTemplate,
  deleteMessageTemplate,
  draftIntroMessage,
  draftMessageTemplate,
  getMessageTemplates,
  personalizeMessageTemplates,
  updateDefaultReply,
  updateMessageTemplate,
  type MessageTemplate
} from '@/lib/api/provider/messageTemplates';
import { queryKeys } from '@/lib/query';

export const useMessageTemplates = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.provider.messageTemplates.list(),
    queryFn: getMessageTemplates,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true
  });

export const useCreateMessageTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; body: string }) =>
      createMessageTemplate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.messageTemplates.all()
      });
    }
  });
};

export const useUpdateMessageTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number;
      title?: string;
      body?: string;
    }) => updateMessageTemplate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.messageTemplates.all()
      });
    }
  });
};

export const useDeleteMessageTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMessageTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.messageTemplates.all()
      });
    }
  });
};

export const usePersonalizeMessageTemplates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => personalizeMessageTemplates(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.messageTemplates.all()
      });
    }
  });
};

export const useDraftMessageTemplate = () =>
  useMutation({
    mutationFn: (input: { brief: string; title?: string }) =>
      draftMessageTemplate(input)
  });

export const useDraftIntroMessage = () =>
  useMutation({
    mutationFn: (input: { brief: string }) => draftIntroMessage(input)
  });

export const useUpdateDefaultReply = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      ...input
    }: {
      sourceId: number;
      title?: string;
      body: string;
    }) => updateDefaultReply(sourceId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.messageTemplates.all()
      });
    }
  });
};

export type { MessageTemplate };
